import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createTrustSafetyApi } from "../api";
import { createUsersApi } from "../../users/api";
import {
  allowedAppealActions,
  allowedCaseActions,
  allowedEnforcementActions,
  allowedReportActions,
  appealStatusTone,
  caseStatusTone,
  enforcementStatusTone,
  formatLabel,
  reportStatusTone,
  severityTone,
} from "../labels";
import type {
  AppealAction,
  AppealDto,
  CaseEvidenceDto,
  CasePolicyDto,
  CaseSubjectDto,
  EnforcementDto,
  EnforcementLifecycleAction,
  InternalNoteDto,
  ModerationCaseAction,
  ModerationCaseDto,
  ReportAction,
  ReportDto,
} from "../types";
import {
  AddEvidenceDialog,
  AddSubjectDialog,
  ConfirmActionDialog,
  CreateNoteDialog,
  EnforcementLifecycleDialog,
  MapPolicyDialog,
  ProposeEnforcementDialog,
  ReportLinkOpenDialog,
} from "../components/TrustSafetyDialogs";

export function ReportDetailPage() {
  const { reportId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const usersApi = useMemo(() => createUsersApi(client), [client]);
  const toast = useToast();

  const [report, setReport] = useState<ReportDto | null>(null);
  // The reported subject (who the report is ABOUT), only when subject_type
  // is 'user' — resolved via the existing Users read, best-effort. This is
  // deliberately NOT done for reporter_user_id below: that identity carries
  // an explicit "protected handling" constraint (see the render below) that
  // predates this change, and resolving/displaying it more prominently
  // would work against exactly the protection that note calls out.
  const [subjectName, setSubjectName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Trust & Safety", to: "/app/trust-safety" },
    { label: "Reports", to: "/app/trust-safety/reports" },
    { label: report?.id ?? "Report" },
  ]);

  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getReport(reportId);
      setReport(result.report);
      setSubjectName(null);
      if (result.report.subject_type === "user") {
        void usersApi
          .getDetail(result.report.subject_id)
          .then((r) => setSubjectName(r.user.display_name))
          .catch(() => {});
      }
    } catch (err) {
      setError(apiErrorMessage(err));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [api, usersApi, reportId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading Report…" />;
  if (error || !report) {
    return (
      <EmptyState
        title="Report unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedReportActions(report.status).filter(
    (action) => action !== "link_or_open",
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Report ${report.id}`}
        description={`${formatLabel(report.report_type)} · submitted ${report.submitted_at}`}
        actions={
          <Flex gap={8} wrap>
            {allowedReportActions(report.status).includes("link_or_open") ? (
              <Button variant="secondary" onClick={() => setLinkOpen(true)}>
                Link or open case
              </Button>
            ) : null}
            {actions.length > 0 ? (
              <Button variant="secondary" onClick={() => setActionOpen(true)}>
                Status actions
              </Button>
            ) : null}
            {report.moderation_case_id ? (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    `/app/trust-safety/cases/${report.moderation_case_id}`,
                  )
                }
              >
                Open case
              </Button>
            ) : null}
          </Flex>
        }
      />

      <ActionBar
        left={
          <StatusChip tone={reportStatusTone(report.status)}>
            {formatLabel(report.status)}
          </StatusChip>
        }
      />

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Intake
          </Text>
          <Text role="body" as="p">
            Subject: {report.subject_type} /{" "}
            {report.subject_type === "user" ? (
              <Link to={`/app/users/${report.subject_id}`}>
                {subjectName ?? report.subject_id}
              </Link>
            ) : (
              report.subject_id
            )}
          </Text>
          <Text role="body" as="p">
            Reporter User ID:{" "}
            {report.reporter_user_id
              ? `${report.reporter_user_id} (protected handling)`
              : "—"}
          </Text>
          <Text role="body" as="p">
            Evidence reference: {report.evidence_reference ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Reporter protection remains a handling constraint. Do not invent
            Community ownership or membership changes from this screen.
          </Text>
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={actionOpen}
        title="Report status action"
        notice="Triage, resolve, and close require a rationale for audit."
        actions={actions}
        busy={busy}
        error={actionError}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyReportAction(
              report.id,
              input.action as ReportAction,
              {
                ...(input.reason ? { reason: input.reason } : {}),
              },
            );
            toast.push({
              title: `Report ${formatLabel(input.action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ReportLinkOpenDialog
        open={linkOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setLinkOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyReportAction(report.id, "link_or_open", input);
            toast.push({ title: "Report linked or opened", tone: "success" });
            setLinkOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function CaseDetailPage() {
  const { caseId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const toast = useToast();

  const [moderationCase, setModerationCase] =
    useState<ModerationCaseDto | null>(null);
  const [subjects, setSubjects] = useState<CaseSubjectDto[]>([]);
  const [evidence, setEvidence] = useState<CaseEvidenceDto[]>([]);
  const [policies, setPolicies] = useState<CasePolicyDto[]>([]);
  const [notes, setNotes] = useState<InternalNoteDto[]>([]);
  const [enforcements, setEnforcements] = useState<EnforcementDto[]>([]);
  const [nestedError, setNestedError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [caseActionOpen, setCaseActionOpen] = useState(false);
  const [assignOwnerId, setAssignOwnerId] = useState("");
  const [decisionSummary, setDecisionSummary] = useState("");
  const [subjectOpen, setSubjectOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [enforcementAction, setEnforcementAction] = useState<{
    id: string;
    action: EnforcementLifecycleAction;
    actionType: EnforcementDto["action_type"];
  } | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Trust & Safety", to: "/app/trust-safety" },
    { label: "Cases", to: "/app/trust-safety/cases" },
    { label: moderationCase?.id ?? "Case" },
  ]);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    setNestedError(null);
    const [
      caseResult,
      subjectResult,
      evidenceResult,
      policyResult,
      noteResult,
      enforcementResult,
    ] = await Promise.allSettled([
      api.getCase(caseId),
      api.listSubjects(caseId, { limit: 50 }),
      api.listEvidence(caseId, { limit: 50, sort: "-added_at" }),
      api.listPolicies(caseId, { limit: 50, sort: "-mapped_at" }),
      api.listCaseNotes(caseId, { limit: 50, sort: "-created_at" }),
      api.listCaseEnforcements(caseId, { limit: 50, sort: "-effective_from" }),
    ]);

    if (caseResult.status === "fulfilled") {
      setModerationCase(caseResult.value.moderation_case);
      setDecisionSummary(
        caseResult.value.moderation_case.decision_summary ?? "",
      );
      setAssignOwnerId(caseResult.value.moderation_case.owner_user_id ?? "");
    } else {
      setModerationCase(null);
      setError(apiErrorMessage(caseResult.reason));
    }

    const failures: string[] = [];
    if (subjectResult.status === "fulfilled") {
      setSubjects(subjectResult.value.subjects);
    } else {
      setSubjects([]);
      failures.push("subjects");
    }
    if (evidenceResult.status === "fulfilled") {
      setEvidence(evidenceResult.value.evidence);
    } else {
      setEvidence([]);
      failures.push("evidence");
    }
    if (policyResult.status === "fulfilled") {
      setPolicies(policyResult.value.policy_mappings);
    } else {
      setPolicies([]);
      failures.push("policies");
    }
    if (noteResult.status === "fulfilled") {
      setNotes(noteResult.value.internal_notes);
    } else {
      setNotes([]);
      failures.push("notes");
    }
    if (enforcementResult.status === "fulfilled") {
      setEnforcements(enforcementResult.value.enforcement_actions);
    } else {
      setEnforcements([]);
      failures.push("enforcements");
    }
    if (failures.length > 0) {
      setNestedError(`Unable to load ${failures.join(", ")}.`);
    }
    setLoading(false);
  }, [api, caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(label: string, work: () => Promise<void>) {
    setBusy(true);
    setActionError(null);
    try {
      await work();
      toast.push({ title: label, tone: "success" });
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading Moderation Case…" />;
  if (error || !moderationCase) {
    return (
      <EmptyState
        title="Case unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const caseActions = allowedCaseActions(moderationCase.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Case ${moderationCase.id}`}
        description={`Opened ${moderationCase.opened_at}`}
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              disabled={caseActions.length === 0}
              onClick={() => setCaseActionOpen(true)}
            >
              Case actions
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/trust-safety/reports?moderation_case_id=${moderationCase.id}`,
                )
              }
            >
              Related reports
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/trust-safety/appeals?source_type=moderation_case&source_id=${moderationCase.id}`,
                )
              }
            >
              Appeals
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} wrap>
            <StatusChip tone={caseStatusTone(moderationCase.status)}>
              {formatLabel(moderationCase.status)}
            </StatusChip>
            <StatusChip tone={severityTone(moderationCase.severity)}>
              {formatLabel(moderationCase.severity)}
            </StatusChip>
          </Flex>
        }
      />

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}
      {nestedError ? (
        <Alert tone="warning" title="Some nested lists failed">
          {nestedError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Case identity
          </Text>
          <Text role="body" as="p">
            Owner: {moderationCase.owner_user_id ?? "Unassigned"}
          </Text>
          <Text role="body" as="p">
            Market: {moderationCase.market_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Closed: {moderationCase.closed_at ?? "—"}
          </Text>
          <Text role="body" as="p">
            Decision summary: {moderationCase.decision_summary ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Support Tickets remain independent (optional note link only). This
            screen does not alter Community ownership, membership, or Community
            lifecycle outside existing Communities endpoints.
          </Text>
        </Stack>
      </Card>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Governed subjects
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setSubjectOpen(true)}
            >
              Add subject
            </Button>
          </Flex>
          {subjects.length === 0 ? (
            <EmptyState title="No subjects" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Role</Th>
                  <Th>Type</Th>
                  <Th>Subject ID</Th>
                </Tr>
              </Thead>
              <Tbody>
                {subjects.map((row) => (
                  <Tr key={row.id}>
                    <Td>{formatLabel(row.subject_role)}</Td>
                    <Td>{row.subject_type}</Td>
                    <Td>{row.subject_id}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Evidence
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEvidenceOpen(true)}
            >
              Add evidence
            </Button>
          </Flex>
          {evidence.length === 0 ? (
            <EmptyState title="No evidence" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Type</Th>
                  <Th>Classification</Th>
                  <Th>File / source</Th>
                  <Th>Added</Th>
                </Tr>
              </Thead>
              <Tbody>
                {evidence.map((row) => (
                  <Tr key={row.id}>
                    <Td>{formatLabel(row.evidence_type)}</Td>
                    <Td>{formatLabel(row.classification)}</Td>
                    <Td>
                      {row.file_asset_id ?? "—"}
                      {row.source_id ? (
                        <Text role="caption" color="muted" as="div">
                          {row.source_type}/{row.source_id}
                        </Text>
                      ) : null}
                    </Td>
                    <Td>
                      {row.added_at}
                      <Text role="caption" color="muted" as="div">
                        by {row.added_by_user_id ?? "—"}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Policy mappings
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPolicyOpen(true)}
            >
              Map Policy
            </Button>
          </Flex>
          {policies.length === 0 ? (
            <EmptyState title="No policy mappings" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Mapping</Th>
                  <Th>Policy</Th>
                  <Th>Version</Th>
                  <Th>Mapped</Th>
                </Tr>
              </Thead>
              <Tbody>
                {policies.map((row) => (
                  <Tr key={row.id}>
                    <Td>{formatLabel(row.mapping_type)}</Td>
                    <Td>{row.policy_id}</Td>
                    <Td>{row.policy_version_id ?? "—"}</Td>
                    <Td>
                      {row.mapped_at}
                      <Text role="caption" color="muted" as="div">
                        by {row.mapped_by_user_id ?? "—"}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Enforcement Actions
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setProposeOpen(true)}
            >
              Propose enforcement
            </Button>
          </Flex>
          <Text role="small" color="secondary" as="p">
            Only API-supported types. Approve/activate permanent ban or
            high-impact actions require Dual Approval. Decision history shows
            status, approver, and reason.
          </Text>
          {enforcements.length === 0 ? (
            <EmptyState title="No enforcement actions" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Subject</Th>
                  <Th>Approver</Th>
                  <Th>Reason</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {enforcements.map((row) => (
                  <Tr key={row.id}>
                    <Td>{formatLabel(row.action_type)}</Td>
                    <Td>
                      <StatusChip tone={enforcementStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>
                      {row.subject_type}
                      <Text role="caption" color="muted" as="div">
                        {row.subject_id}
                      </Text>
                    </Td>
                    <Td>{row.approved_by_user_id ?? "—"}</Td>
                    <Td>{row.reason ?? "—"}</Td>
                    <Td>
                      <Flex gap={4} wrap>
                        {allowedEnforcementActions(row.status).map((action) => (
                          <Button
                            key={action}
                            size="sm"
                            variant={
                              action === "activate" || action === "reverse"
                                ? "danger"
                                : "secondary"
                            }
                            onClick={() =>
                              setEnforcementAction({
                                id: row.id,
                                action,
                                actionType: row.action_type,
                              })
                            }
                          >
                            {formatLabel(action)}
                          </Button>
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Internal notes
            </Text>
            <Button size="sm" variant="secondary" onClick={() => setNoteOpen(true)}>
              Add note
            </Button>
          </Flex>
          {notes.length === 0 ? (
            <EmptyState title="No internal notes" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Classification</Th>
                  <Th>Body</Th>
                  <Th>Author</Th>
                  <Th>Created</Th>
                </Tr>
              </Thead>
              <Tbody>
                {notes.map((row) => (
                  <Tr key={row.id}>
                    <Td>{formatLabel(row.classification)}</Td>
                    <Td>{row.body_reference}</Td>
                    <Td>{row.author_user_id ?? "—"}</Td>
                    <Td>{row.created_at}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <ConfirmActionDialog
        open={caseActionOpen}
        title="Moderation Case action"
        notice="Assign updates owner without changing status. Close and action should include a decision summary when available."
        actions={caseActions}
        busy={busy}
        error={actionError}
        extraFields={
          <>
            <Input
              label="Owner User ID (for assign)"
              value={assignOwnerId}
              onChange={(event) => setAssignOwnerId(event.target.value)}
            />
            <Textarea
              label="Decision summary (for action / close)"
              value={decisionSummary}
              onChange={(event) => setDecisionSummary(event.target.value)}
            />
          </>
        }
        onClose={() => {
          setCaseActionOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run(`Case ${formatLabel(input.action)}`, async () => {
            await api.applyCaseAction(
              moderationCase.id,
              input.action as ModerationCaseAction,
              {
                ...(input.reason ? { reason: input.reason } : {}),
                ...(input.action === "assign" && assignOwnerId.trim()
                  ? { owner_user_id: assignOwnerId.trim() }
                  : {}),
                ...(decisionSummary.trim()
                  ? { decision_summary: decisionSummary.trim() }
                  : {}),
              },
            );
            setCaseActionOpen(false);
          });
        }}
      />

      <AddSubjectDialog
        open={subjectOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setSubjectOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Subject added", async () => {
            await api.addSubject(moderationCase.id, input);
            setSubjectOpen(false);
          });
        }}
      />

      <AddEvidenceDialog
        open={evidenceOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setEvidenceOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Evidence added", async () => {
            await api.addEvidence(moderationCase.id, input);
            setEvidenceOpen(false);
          });
        }}
      />

      <MapPolicyDialog
        open={policyOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setPolicyOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Policy mapped", async () => {
            await api.mapPolicy(moderationCase.id, input);
            setPolicyOpen(false);
          });
        }}
      />

      <ProposeEnforcementDialog
        open={proposeOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setProposeOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Enforcement proposed", async () => {
            await api.proposeEnforcement(moderationCase.id, input);
            setProposeOpen(false);
          });
        }}
      />

      <EnforcementLifecycleDialog
        open={enforcementAction !== null}
        action={enforcementAction?.action ?? null}
        actionType={enforcementAction?.actionType ?? null}
        busy={busy}
        error={actionError}
        onClose={() => {
          setEnforcementAction(null);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          if (!enforcementAction) return;
          const current = enforcementAction;
          await run(
            `Enforcement ${formatLabel(current.action)}`,
            async () => {
              await api.applyEnforcementAction(
                current.id,
                current.action,
                input,
              );
              setEnforcementAction(null);
            },
          );
        }}
      />

      <CreateNoteDialog
        open={noteOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setNoteOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Note added", async () => {
            await api.createCaseNote(moderationCase.id, input);
            setNoteOpen(false);
          });
        }}
      />
    </Stack>
  );
}

export function AppealDetailPage() {
  const { appealId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createTrustSafetyApi(client), [client]);
  const toast = useToast();

  const [appeal, setAppeal] = useState<AppealDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [reviewerId, setReviewerId] = useState("");
  const [decisionReason, setDecisionReason] = useState("");

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Trust & Safety", to: "/app/trust-safety" },
    { label: "Appeals", to: "/app/trust-safety/appeals" },
    { label: appeal?.id ?? "Appeal" },
  ]);

  const load = useCallback(async () => {
    if (!appealId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getAppeal(appealId);
      setAppeal(result.appeal);
      setReviewerId(result.appeal.assigned_reviewer_user_id ?? "");
      setDecisionReason(result.appeal.decision_reason ?? "");
    } catch (err) {
      setError(apiErrorMessage(err));
      setAppeal(null);
    } finally {
      setLoading(false);
    }
  }, [api, appealId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading Appeal…" />;
  if (error || !appeal) {
    return (
      <EmptyState
        title="Appeal unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedAppealActions(appeal.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Appeal ${appeal.id}`}
        description={`${formatLabel(appeal.source_type)} · submitted ${appeal.submitted_at}`}
        actions={
          actions.length > 0 ? (
            <Button variant="secondary" onClick={() => setActionOpen(true)}>
              Review actions
            </Button>
          ) : null
        }
      />

      <ActionBar
        left={
          <StatusChip tone={appealStatusTone(appeal.status)}>
            {formatLabel(appeal.status)}
          </StatusChip>
        }
      />

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Appeal record
          </Text>
          <Text role="body" as="p">
            Source: {formatLabel(appeal.source_type)} / {appeal.source_id}
          </Text>
          <Text role="body" as="p">
            Appellant: {appeal.appellant_user_id}
          </Text>
          <Text role="body" as="p">
            Assigned reviewer: {appeal.assigned_reviewer_user_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Decided: {appeal.decided_at ?? "—"}
          </Text>
          <Text role="body" as="p">
            Decision reason: {appeal.decision_reason ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Independent review from the original high-impact decision. For
            enforcement sources, reviewer independence vs approved_by is
            enforced by the API.
          </Text>
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={actionOpen}
        title="Appeal action"
        notice="Decide actions require a decision reason. Assign reviewer on start_review when needed."
        actions={actions}
        busy={busy}
        error={actionError}
        requireReason={false}
        extraFields={
          <>
            <Input
              label="Assigned reviewer User ID"
              value={reviewerId}
              onChange={(event) => setReviewerId(event.target.value)}
            />
            <Textarea
              label="Decision reason (for decide_*)"
              value={decisionReason}
              onChange={(event) => setDecisionReason(event.target.value)}
            />
          </>
        }
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyAppealAction(
              appeal.id,
              input.action as AppealAction,
              {
                ...(input.reason ? { reason: input.reason } : {}),
                ...(reviewerId.trim()
                  ? { assigned_reviewer_user_id: reviewerId.trim() }
                  : {}),
                ...(decisionReason.trim()
                  ? { decision_reason: decisionReason.trim() }
                  : {}),
              },
            );
            toast.push({
              title: `Appeal ${formatLabel(input.action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}
